import { BaseQueryApi, BaseQueryFn, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/dist/query/react';

import { boatActions } from '@/entities/boat';
import { userActions } from '@/entities/user';
import { baseQuery } from '@/shared/api/common.api';
import { AppStorage } from '@/shared/constants/app-storage.constant';
import { StatusCodeError } from '@/shared/constants/status-code-error.constant';

const REFRESH_PROMISE_GRACE_MS = 1_000;
const RETRY_AFTER_REFRESH_ATTEMPTS = 2;
const RETRY_AFTER_REFRESH_DELAY_MS = 250;

let refreshPromise: Promise<{ access_token: string; refresh_token: string } | null> | null = null;
type BaseQueryResult = Awaited<ReturnType<ReturnType<typeof baseQuery>>>;

const inFlightGetRequests = new Map<string, Promise<BaseQueryResult>>();

type ReauthExtraOptions = {
	skipReauth?: boolean;
};

type RefreshTokens = {
	access_token: string;
	refresh_token: string;
};

const isUnauthorizedError = (error?: { status: number | string; originalStatus?: number }): boolean =>
	Boolean(
		error && (error.status === StatusCodeError.Unauthorized || error.originalStatus === StatusCodeError.Unauthorized),
	);

const stableStringify = (value: unknown): string => {
	if (value === null || typeof value !== 'object') {
		return JSON.stringify(value);
	}

	if (Array.isArray(value)) {
		return `[${value.map(stableStringify).join(',')}]`;
	}

	return `{${Object.entries(value as Record<string, unknown>)
		.sort(([keyA], [keyB]) => keyA.localeCompare(keyB))
		.map(([key, item]) => `${JSON.stringify(key)}:${stableStringify(item)}`)
		.join(',')}}`;
};

const getRequestMethod = (args: string | FetchArgs): string =>
	typeof args === 'string' ? 'GET' : (args.method ?? 'GET').toUpperCase();

const getInFlightGetRequestKey = (apiUrl: string, args: string | FetchArgs): string | null => {
	if (getRequestMethod(args) !== 'GET') {
		return null;
	}

	if (typeof args === 'string') {
		return `${apiUrl}|GET|${args}`;
	}

	return `${apiUrl}|GET|${args.url}|${stableStringify(args.params)}`;
};

const wait = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

const persistRefreshTokens = (tokens: RefreshTokens, api: BaseQueryApi) => {
	localStorage.setItem(AppStorage.Token, tokens.refresh_token);
	localStorage.setItem(AppStorage.AccessToken, tokens.access_token);
	api.dispatch(userActions.setAccessToken(tokens.access_token));
};

export const baseQueryWithReauth: (
	apiUrl: string,
) => BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError, ReauthExtraOptions> =
	(apiUrl: string) => async (args, api, extraOptions) => {
		const isUserApiRequest = apiUrl.includes('/hdata-user/api/');
		const shouldSkipReauth = extraOptions?.skipReauth;

		const executeBaseQuery = (requestArgs: string | FetchArgs): Promise<BaseQueryResult> => {
			const requestKey = getInFlightGetRequestKey(apiUrl, requestArgs);

			if (!requestKey) {
				return Promise.resolve(baseQuery(apiUrl)(requestArgs, api, extraOptions));
			}

			const inFlightRequest = inFlightGetRequests.get(requestKey);
			if (inFlightRequest) {
				return inFlightRequest;
			}

			const request = Promise.resolve(baseQuery(apiUrl)(requestArgs, api, extraOptions));
			inFlightGetRequests.set(requestKey, request);
			void request.finally(() => {
				if (inFlightGetRequests.get(requestKey) === request) {
					inFlightGetRequests.delete(requestKey);
				}
			});

			return request;
		};

		if (!shouldSkipReauth && !isUserApiRequest && refreshPromise) {
			const refreshedTokens = await refreshPromise;
			if (refreshedTokens) {
				persistRefreshTokens(refreshedTokens, api);
			}
		}

		const result = await executeBaseQuery(args);
		const error = result?.error as { status: number | string; originalStatus?: number } | undefined;

		if (error && error.status === StatusCodeError.Locked) {
			localStorage.removeItem(AppStorage.Token);
			localStorage.removeItem(AppStorage.AccessToken);

			api.dispatch(userActions.logout());
		}

		if (!shouldSkipReauth && isUnauthorizedError(error)) {
			const refreshToken = localStorage.getItem(AppStorage.Token);

			if (!refreshPromise) {
				refreshPromise = (async () => {
					const refreshResult = await baseQuery(USER_API_URL)(
						{
							url: 'user/refresh',
							method: 'POST',
							body: { token: refreshToken },
						},
						api,
						extraOptions,
					);

					if (!refreshResult.data) {
						return null;
					}

					const tokens = refreshResult.data as RefreshTokens;
					persistRefreshTokens(tokens, api);
					return tokens;
				})();
			}

			const currentRefreshPromise = refreshPromise;
			const refreshedTokens = await currentRefreshPromise;

			if (refreshPromise === currentRefreshPromise) {
				setTimeout(() => {
					if (refreshPromise === currentRefreshPromise) {
						refreshPromise = null;
					}
				}, REFRESH_PROMISE_GRACE_MS);
			}

			if (refreshedTokens) {
				persistRefreshTokens(refreshedTokens, api);

				for (let attempt = 0; attempt <= RETRY_AFTER_REFRESH_ATTEMPTS; attempt += 1) {
					const retryResult = await executeBaseQuery(args);
					const retryError = retryResult?.error as { status: number | string; originalStatus?: number } | undefined;
					if (!isUnauthorizedError(retryError) || attempt === RETRY_AFTER_REFRESH_ATTEMPTS) {
						return retryResult;
					}

					await wait(RETRY_AFTER_REFRESH_DELAY_MS * (attempt + 1));
				}
			} else {
				api.dispatch(userActions.logout());
				api.dispatch(boatActions.resetBoatById());

				if (!AUTH_V2_ENABLED) {
					await baseQuery(USER_API_URL)({ url: 'grafana/logout', method: 'POST' }, api, extraOptions);
				}
			}
		}
		return result;
	};
